import { type RecipeV2Doc, type RecipeV2Summary } from '@ctcells/api'
import { PATHS } from '@ctcells/core'
import { type MessageKey } from '@ctcells/i18n'

import { cellText, type StandardTable } from '../recipes/recipeNewStandardModel.ts'

/**
 * 작업 준비 › 템플릿의 순수 모델 — 🔴 **템플릿은 「레시피」(서버 표준 레시피 한 벌) 하나뿐이다**(eric 2026-10-26 「응.. 다 빼줘」).
 * 데크 · 작업 · 랩웨어 · 시약 템플릿(`/templates/{kind}`)은 화면에서 걷었다 — 그것을 쓰는 서버 창구(`POST /recipes/compose`)를 부르는
 * 화면이 없었다. 서버 데이터 · API 는 그대로다(조회 훅 `useTemplates` 는 시작 전 점검의 설치 정의 넣기가 랩웨어 목록으로 쓴다).
 * 옛 주소(`/recipes/templates/<labware|reagent|work|deck>…`)는 템플릿 목록으로 보낸다(`consoleTemplateViews.tsx`).
 */

/**
 * 「레시피」 템플릿 주소 조각(eric 2026-10-12 「작업 준비에서.. 레시피 템플릿 탭을 나 만들어줘.. 저기서 표현해줘」).
 * 🔴 장비 전 구간(원심 · 어셈블러 · 염색)이 든 «레시피 한 벌»은 서버의 표준 레시피(`GET /recipes/standard`)가 유일한 출처다.
 *    등록 · 수정 창구는 서버에 없다(읽기 전용 · BR-129).
 */
export const RECIPE_TEMPLATE_TAB = 'recipe' as const

/** 「레시피」 템플릿 주소 — `/recipes/templates/recipe` */
export const recipeTemplatesPath = `${PATHS.templates}/${RECIPE_TEMPLATE_TAB}`

/** 표준 레시피 한 벌의 식별자(주소 조각) — 서버 문서엔 식별자 칸이 없어 화면이 이 하나로 부른다 */
export const STANDARD_RECIPE_TEMPLATE_ID = 'standard'

/** 「레시피」 템플릿 한 건(오른쪽 판) 주소 */
export const recipeTemplateDetailPath = (id: string): string =>
  `${recipeTemplatesPath}/${encodeURIComponent(id)}`

/** 「레시피 템플릿」 표 한 행 — 문서의 `metadata` 에서(2026-10-12) */
export interface RecipeTemplateRow {
  id: string
  name: string
  category: string
}

/** 문서 → 표 한 행. 이름이 없으면 식별자(지어내지 않는다) */
export function recipeTemplateRow(doc: RecipeV2Doc): RecipeTemplateRow {
  const meta = (doc['metadata'] ?? {}) as Record<string, unknown>
  const text = (v: unknown) => (typeof v === 'string' && v.trim() !== '' ? v : undefined)
  return {
    id: STANDARD_RECIPE_TEMPLATE_ID,
    name: text(meta['protocolName']) ?? STANDARD_RECIPE_TEMPLATE_ID,
    category: [text(meta['category']), text(meta['subcategory'])]
      .filter((v): v is string => v !== undefined)
      .join(' · '),
  }
}

/**
 * 「레시피」 템플릿 오른쪽 판의 표 셋(eric 2026-10-13 「이 부분 너가 봐도 이상하지? 우리 화면 컨셉에 맞게 수정해줘..」) — 종전엔
 * `s01 gel_dpbs · 워싱용액` · `P1 A1·B1, …` 처럼 «·» 로 이어 붙인 맨 글자였다. 레시피 추가 창의 표(`recipeNewStandardModel` `StandardTable`)와
 * 같은 모양 · 같은 칸 글자(`cellText`)로 연다.
 * 🔴 열은 서버 요약(`POST /recipes/summary`)에 실제로 있는 키뿐이다. 작업의 `params` 는 펴서 열로 올린다(첫 등장 차례) —
 *    단계마다 없는 칸은 빈 글자(화면이 —). 모르는 키는 키 이름이 열 머리다(지어내지 않는다).
 */
const STAGE_LEAD: readonly string[] = ['stage', 'name', 'shape', 'reagent_role']

const STAGE_LABEL: Readonly<Record<string, MessageKey>> = {
  stage: 'recipeTemplate.col.stage',
  name: 'column.name',
  shape: 'template.field.shape',
  reagent_role: 'template.field.reagent_role',
  volume: 'recipeEdit.f.volumeUl',
  liquidClass: 'recipeEdit.f.liquidClass',
  rpm: 'recipeEdit.f.shakeRpm',
  seconds: 'recipeEdit.f.shakeSec',
  repeats: 'recipeEdit.f.repetitions',
  waste_stream: 'recipeEdit.f.wasteStream',
}

/** 작업 표 — 단계 · 이름 · 모양 · 시약 역할 + `params` 의 키 */
export function recipeTemplateStageTable(summary: RecipeV2Summary): StandardTable {
  const records = summary.stages.map((stage) => {
    const { params, ...top } = stage
    const fields: Record<string, unknown> = { ...top, ...params }
    return { key: stage.stage, fields }
  })
  const seen: string[] = [...STAGE_LEAD]
  for (const { fields } of records)
    for (const k of Object.keys(fields)) if (!seen.includes(k)) seen.push(k)
  return {
    columns: seen.map((id) => ({ id, label: STAGE_LABEL[id] })),
    rows: records.map(({ key, fields }) => ({
      key,
      cells: Object.fromEntries(seen.map((id) => [id, cellText(fields[id])])),
    })),
  }
}
